import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react'

import { yieldToMain } from '../utils/yieldToMain'

/**
 * Optimistic selection primitives for filter-style selectors whose "selected" highlight is derived
 * from state that ALSO drives a heavy re-render (the CLP filter panel / product grid). Calling the
 * real change handler synchronously paints the highlight in the SAME task as that heavy work, so the
 * highlight can't land first — inflating INP on every filter click.
 *
 * The split, in browser-timeline order: paint the optimistic UI immediately → `yieldToMain()` so that
 * paint can flush → run the heavy commit (inside `startTransition`, so the follow-up re-render stays
 * interruptible). The YIELD — not the transition alone — is what unblocks the paint; `startTransition`
 * only lowers the priority of the resulting re-render, it does not move synchronous JS off the click
 * task. {@link useDeferredCommit} is that shared mechanism; the two hooks below add the selection
 * semantics (supersede vs accumulate) on top of it.
 *
 * `enabled` gates the optimization (Statsig `clp_responsive_filters`). When false BOTH hooks are
 * transparent passthroughs — the handler runs synchronously and the effective state is just the
 * committed state — so the gate-off bucket is byte-for-byte identical to the pre-optimization code.
 * All React hooks are still called unconditionally so the Rules of Hooks hold in either bucket.
 *
 * These live in `quince-site-common` so both PDP option selectors and CLP filter atoms in
 * `quince-components` can consume them without duplicating scheduling logic or introducing a
 * `components → pdp` import.
 */

/**
 * The shared scheduling mechanism: run `commit` after a paint opportunity, inside a transition.
 *
 * `await yieldToMain()` lets the optimistic paint flush first; the commit is then skipped if the
 * component has unmounted (so a deferred `onSelect`/`onToggle` — which navigates via `updateQuery` —
 * can't fire after the user has left) or if `shouldCommit` reports the work is stale (supersede).
 * The mount guard lives here so it is defined once rather than duplicated per hook.
 *
 * Exported so `useFilterState.handleCheckboxFilterChange` can defer its `trackEvent` + `updateQuery`
 * calls under `optimisticPaint` without duplicating the yield + mount-guard boilerplate.
 */
export function useDeferredCommit(): (commit: () => void, shouldCommit?: () => boolean) => void {
  const [, startTransition] = useTransition()

  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  return useCallback(
    (commit: () => void, shouldCommit?: () => boolean) => {
      void (async () => {
        await yieldToMain()
        if (!isMountedRef.current) return
        if (shouldCommit && !shouldCommit()) return
        startTransition(commit)
      })()
    },
    [startTransition],
  )
}

/**
 * Small primitive for components with custom optimistic-selection semantics.
 *
 * It owns the shared mechanics: optimistic override state, reset-on-committed-key
 * reconciliation, and "latest deferred commit wins" sequencing. Callers still decide
 * what value to paint and what the deferred commit should do.
 */
export function useResettableOptimisticValue<T>(
  resetKey: unknown,
  enabled = false,
): {
  optimisticValue: T | undefined
  setOptimisticValueAndDeferCommit: (value: T, commit: () => void) => void
} {
  const [optimisticValue, setOptimisticValue] = useState<T>()
  const deferCommit = useDeferredCommit()
  const latestSeqRef = useRef(0)

  useEffect(() => {
    if (!enabled) return
    setOptimisticValue(undefined)
  }, [resetKey, enabled])

  const setOptimisticValueAndDeferCommit = useCallback(
    (value: T, commit: () => void) => {
      if (!enabled) {
        commit()
        return
      }

      const seq = ++latestSeqRef.current
      setOptimisticValue(value)
      deferCommit(commit, () => seq === latestSeqRef.current)
    },
    [deferCommit, enabled],
  )

  return { optimisticValue, setOptimisticValueAndDeferCommit }
}

/**
 * Single-select (radio) optimistic selection — sort, single-value facets. Supersede semantics: rapid
 * back-to-back picks are debounced to the latest via a monotonic counter, so a superseded selection's
 * `onSelect` (and its analytics) is skipped — avoiding a wasted recompute and the stale-reconcile
 * flicker that would briefly revert the highlight. The optimistic value clears once the committed
 * `selectedValue` catches up, leaving a single source of truth.
 *
 * NOTE: when enabled, `onSelect` runs asynchronously (after a yield). Tests that assert it fired must
 * await (`findBy*` / `waitFor`), not assert synchronously after the click.
 */
export function useOptimisticSelection(
  selectedValue: string | undefined,
  onSelect: (value: string) => void,
  enabled = false,
): { effectiveSelected: string | undefined; handleSelect: (value: string) => void } {
  const [optimisticSelected, setOptimisticSelected] = useState<string>()
  const deferCommit = useDeferredCommit()

  // Monotonic counter so a deferred task can tell whether a newer selection has superseded it.
  const latestSeqRef = useRef(0)

  // Reconcile once the real selectedValue catches up (or changes upstream). Inert when disabled —
  // optimisticSelected is never written in that bucket.
  useEffect(() => {
    if (!enabled) return
    setOptimisticSelected(undefined)
  }, [selectedValue, enabled])

  const handleSelect = useCallback(
    (value: string) => {
      if (!enabled) {
        onSelect(value)
        return
      }
      const seq = ++latestSeqRef.current
      // Paint the picked value immediately, then commit after the yield only if still the latest pick.
      setOptimisticSelected(value)
      deferCommit(
        () => {
          onSelect(value)
        },
        () => seq === latestSeqRef.current,
      )
    },
    [onSelect, enabled, deferCommit],
  )

  return { effectiveSelected: enabled ? (optimisticSelected ?? selectedValue) : selectedValue, handleSelect }
}

/**
 * Multi-select (toggle) optimistic selection — colour swatches and any other accumulating filter.
 *
 * Accumulate semantics, NOT supersede: clicking red then blue must yield `{red, blue}` and every
 * toggle must apply (none dropped) — so it does not debounce-to-latest. The optimistic state is a
 * per-value overlay layered over `committedValues`; the effective selection is committed ∪ {added} ∖
 * {removed}. Reconcile is PER VALUE: when the committed state catches up for a value, only that
 * value's overlay entry is dropped — a still-pending pick (e.g. blue after red has committed) is kept,
 * so its highlight doesn't flicker off then back on.
 */
export function useOptimisticToggleSelection(
  committedValues: string[],
  onToggle: (value: string, desiredSelected?: boolean) => void,
  enabled = false,
): { effectiveValues: string[]; handleToggle: (value: string) => void } {
  // Per-value desired selected state, layered over committedValues. `true` = optimistically added,
  // `false` = optimistically removed.
  const [optimisticOverlay, setOptimisticOverlay] = useState<Record<string, boolean>>({})
  const optimisticOverlayRef = useRef<Record<string, boolean>>({})
  const deferCommit = useDeferredCommit()

  // Latest committed values, read synchronously inside the toggle handler so the desired state is
  // computed against fresh committed state even before a re-render.
  const committedRef = useRef(committedValues)
  committedRef.current = committedValues

  // Per-value monotonic seq map: a later tap on the SAME value supersedes the earlier pending commit
  // for that value, while different-value taps are independent.
  const valueSeqRef = useRef<Map<string, number>>(new Map())

  // Per-value reconcile: drop only the overlay entries the committed state now agrees with, keeping
  // not-yet-committed picks so they don't flicker. Inert when disabled — the overlay is never written.
  useEffect(() => {
    if (!enabled) return
    const committedSet = new Set(committedValues)
    let changed = false
    const next: Record<string, boolean> = {}
    for (const [value, desired] of Object.entries(optimisticOverlayRef.current)) {
      if (committedSet.has(value) === desired) {
        changed = true // committed caught up for this value → drop it.
      } else {
        next[value] = desired
      }
    }
    if (!changed) return
    optimisticOverlayRef.current = next
    setOptimisticOverlay(next)
  }, [committedValues, enabled])

  const effectiveValues = useMemo(() => {
    if (!enabled) return committedValues
    const set = new Set(committedValues)
    for (const [value, desired] of Object.entries(optimisticOverlay)) {
      if (desired) set.add(value)
      else set.delete(value)
    }
    return Array.from(set)
  }, [enabled, committedValues, optimisticOverlay])

  const handleToggle = useCallback(
    (value: string) => {
      if (!enabled) {
        onToggle(value)
        return
      }
      // Compute desired state synchronously (committed + pending overlay) and flip — so the swatch
      // highlight paints within this click task.
      const set = new Set(committedRef.current)
      for (const [v, desired] of Object.entries(optimisticOverlayRef.current)) {
        if (desired) set.add(v)
        else set.delete(v)
      }
      const desiredSelected = !set.has(value)
      const nextOverlay = { ...optimisticOverlayRef.current, [value]: desiredSelected }
      optimisticOverlayRef.current = nextOverlay
      setOptimisticOverlay(nextOverlay)

      // Per-value supersede: a later tap on the same value increments the seq for that value,
      // causing any still-pending deferred commit for this value to be skipped.
      const seq = (valueSeqRef.current.get(value) ?? 0) + 1
      valueSeqRef.current.set(value, seq)

      deferCommit(
        () => {
          // Net-zero check: if committed state already matches the desired outcome (e.g. double-tap),
          // skip onToggle entirely and remove the stale overlay entry.
          if (desiredSelected === committedRef.current.includes(value)) {
            // Use rest-spread to drop the key without dynamic delete (satisfies no-dynamic-delete).
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [value]: _dropped, ...rest } = optimisticOverlayRef.current
            optimisticOverlayRef.current = rest
            setOptimisticOverlay(rest)
            return
          }
          onToggle(value, desiredSelected)
        },
        () => seq === valueSeqRef.current.get(value),
      )
    },
    [onToggle, enabled, deferCommit],
  )

  return { effectiveValues, handleToggle }
}
